import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Package, Ruler, User, Plus, CreditCard, Calendar,
  AlertCircle, Clock, Edit, Trash2, X, List, Mail, Phone
} from 'lucide-react';
import moment from 'moment';
import { sessionsAPI, studentsAPI, studentSubscriptionsAPI } from '../services/api';
import { useApp } from '../contexts/AppContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../App';
import { useToast } from '../contexts/ToastContext';
// import LoadingSpinner from '../components/ui/LoadingSpinner';
import StudentPackageAssignmentModal from '../components/student-details/StudentPackageAssignmentModal';
import StudentMeasurementsTab from '../components/student-details/StudentMeasurementsTab';
import StudentDetailsTab from '../components/student-details/StudentDetailsTab';
import PaymentModal from '../components/ui/PaymentModal';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../components/HasPermission';

const StudentDetailsPage = () => {
  const { colors, isDark } = useTheme();
  const { studentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganizationId } = useApp();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const toast = useToast();

  const organizationId = location.state?.organizationId || selectedOrganizationId;
  const [student, setStudent] = useState(location.state?.student || null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(!student);
  const [activeTab, setActiveTab] = useState('packages');
  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [subscriptionSessions, setSubscriptionSessions] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      if (!organizationId) {
        setError('Organization not selected');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch student data if not available
        if (!student) {
          const studentResponse = await studentsAPI.getById(organizationId, studentId);
          setStudent(studentResponse.data.data);
        }

        // Fetch subscriptions
        const subscriptionsResponse = await studentSubscriptionsAPI.getByStudent(organizationId, studentId);
        const subscriptionsData = subscriptionsResponse.data.data || [];
        setSubscriptions(subscriptionsData);

        // Fetch sessions sequentially to prevent AWS API Gateway burst throttling (503/CORS)
        if (subscriptionsData.length > 0) {
          const sessionsData = {};

          for (const sub of subscriptionsData) {
            try {
              const sessions = await sessionsAPI.getBySubscriptionId(organizationId, sub.id);
              sessionsData[sub.id] = sessions.data.data || [];
            } catch (err) {
              console.error(`Failed to load sessions for subscription ${sub.id}:`, err);
              sessionsData[sub.id] = []; // Fallback to prevent page crash
            }
          }

          setSubscriptionSessions(sessionsData);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(!student ? 'Failed to load student data' : 'Failed to load subscriptions');
      } finally {
        setLoading(false);
      }
    };

    if (organizationId && studentId) {
      fetchData();
    }
  }, [organizationId, studentId, student]);

  const handlePackageAssigned = async () => {
    try {
      const subscriptionsResponse = await studentSubscriptionsAPI.getByStudent(organizationId, studentId);
      setSubscriptions(subscriptionsResponse.data.data || []);
      setIsPackageModalOpen(false);
    } catch (err) {
      console.error('Error refreshing subscriptions:', err);
    }
  };

  const handlePayment = (subscription) => {
    setSelectedSubscription(subscription);
    setIsPaymentModalOpen(true);
  };

  const handlePaymentComplete = async () => {
    try {
      const response = await studentSubscriptionsAPI.getByStudent(organizationId, studentId);
      setSubscriptions(response.data.data || []);
    } catch (err) {
      console.error('Error refreshing subscriptions:', err);
    }
    setIsPaymentModalOpen(false);
    setSelectedSubscription(null);
  };

  // Helper to calculate accurate session stats
  const getSubscriptionStats = (subscription) => {
    const sessions = subscriptionSessions[subscription.id] || [];
    // Canonical "used" = sessions that consumed a credit (isDeducted), matching the
    // backend ledger. Fall back to the status heuristic for legacy rows lacking the
    // field (credit-lifecycle-root-cause.md I-1).
    const usedSessions = sessions.filter(s =>
      typeof s.isDeducted === 'boolean' ? s.isDeducted : s.status !== 'cancelled'
    ).length;

    // Total capacity = Base + Bonus
    // We default to 0 to be safe
    const baseTotal = subscription.totalSessions || 0;
    const bonus = subscription.bonusSessions || 0;
    const totalCapacity = baseTotal + bonus;

    // Remaining = Capacity - Used
    // Ensure we don't return negative just in case
    const remaining = Math.max(0, totalCapacity - usedSessions);

    // Breakdown of used sessions
    const completed = sessions.filter(s => s.status === 'completed' || s.status === 'confirmed').length;
    const scheduled = sessions.filter(s => s.status === 'scheduled' || s.status === 'pending').length;
    const cancelled = sessions.filter(s => s.status === 'cancelled').length;
    const noShow = sessions.filter(s => s.status === 'no-show').length;

    return {
      used: usedSessions,
      remaining,
      totalCapacity,
      baseTotal,
      breakdown: {
        completed,
        scheduled,
        cancelled,
        noShow,
      }
    };
  };

  const handleEditSubscription = (subscription) => {
    // Calculate remaining sessions accurately using the new helper
    const stats = getSubscriptionStats(subscription);

    setEditingSubscription({
      ...subscription,
      remainingSessions: stats.remaining
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateSubscription = async (subscriptionId, updateData) => {
    try {
      await studentSubscriptionsAPI.update(organizationId, subscriptionId, updateData);
      const response = await studentSubscriptionsAPI.getByStudent(organizationId, studentId);
      setSubscriptions(response.data.data || []);
      setIsEditModalOpen(false);
      setEditingSubscription(null);
    } catch (err) {
      console.error('Error updating subscription:', err);
      toast.error(err.response?.data?.error || 'Failed to update subscription');
    }
  };

  const handleDeleteSubscription = async (subscriptionId) => {
    if (!window.confirm(t('studentProfile.packages.deleteConfirm'))) return;

    try {
      const activeOrgId = student?.organizationId || organizationId;
      await studentSubscriptionsAPI.delete(activeOrgId, subscriptionId);
      const response = await studentSubscriptionsAPI.getByStudent(activeOrgId, studentId);
      setSubscriptions(response.data.data || []);
      toast.success(t('common.success', 'Operation successful'));
    } catch (err) {
      console.error('Error deleting subscription:', err);
      toast.error(err.response?.data?.error || 'Failed to delete subscription');
    }
  };

  const handleViewSessions = (subscription) => {
    navigate(`/student-sessions/${studentId}/${subscription.id}`, {
      state: {
        student,
        subscription,
        organizationId,
        sessions: subscriptionSessions[subscription.id] || []
      }
    });
  };

  const getStatusBadge = (status, type = 'subscription') => {
    const colors = {
      subscription: {
        active: 'bg-green-100 text-green-800',
        expired: 'bg-red-100 text-red-800',
        completed: 'bg-red-100 text-red-800',
        cancelled: 'bg-gray-100 text-gray-800',
        suspended: 'bg-yellow-100 text-yellow-800'
      },
      payment: {
        paid: 'bg-green-100 text-green-800',
        partial: 'bg-yellow-100 text-yellow-800',
        unpaid: 'bg-red-100 text-red-800'
      }
    };

    return colors[type][status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      active: t('organizationDetailsPage.packagesSection.statuses.active'),
      inactive: t('organizationDetailsPage.packagesSection.statuses.inactive'),
      suspended: t('organizationDetailsPage.packagesSection.statuses.suspended'),
      graduated: t('studentDetails.values.graduated'),

      // Subscription statuses
      expired: t('organizationDetailsPage.packagesSection.statuses.completed'), // Legacy
      completed: t('organizationDetailsPage.packagesSection.statuses.completed'),
      cancelled: t('studentDetails.values.cancelled', 'Cancelled') || 'İptal',

      // Payment statuses
      paid: t('packageAssignment.fullyPaid'),
      partial: t('packageAssignment.partiallyPaid'),
      unpaid: t('packageAssignment.unpaid')
    };
    return statusMap[status] || status;
  };

  const getFirstSessionDate = (subscriptionId) => {
    const sessions = subscriptionSessions[subscriptionId] || [];
    if (sessions.length === 0) return t('studentProfile.packages.noSessions');

    const earliestSession = sessions.reduce((earliest, current) =>
      new Date(current.sessionDate) < new Date(earliest.sessionDate) ? current : earliest
    );
    return moment(earliestSession.sessionDate).format('DD/MM/YYYY');
  };

  const getExpiryDate = (subscriptionId, validityPeriod) => {
    const sessions = subscriptionSessions[subscriptionId] || [];
    if (sessions.length === 0) return t('studentProfile.packages.notStarted');

    const earliestDate = moment.min(
      sessions.map(s => moment(s.sessionDate))
    );
    return earliestDate.add(validityPeriod, 'days').format('DD/MM/YYYY');
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-96 ${colors.bgPrimary}`}>
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  if (error && !student) {
    return (
      <div className={`flex flex-col items-center justify-center min-h-96 ${colors.bgPrimary}`}>
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className={`text-lg font-medium ${colors.textPrimary} mb-2`}>{t('organizationDetailsPage.notFound', 'Error Loading Student')}</h3>
        <p className={`${colors.textSecondary} mb-4`}>{error || 'Student not found'}</p>
        <button
          onClick={() => navigate('/students')}
          className={`px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02]`}
        >
          {t('studentProfile.backToStudents')}
        </button>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${colors.bgPrimary}`}>
      {/* New Floating Profile Card Layout */}
      <div className="px-3 sm:px-6 pt-4 sm:pt-6">
        <div className={`${colors.bgTertiary} border ${colors.border} rounded-2xl relative overflow-hidden shadow-sm`}>
          {/* Subtle Background Decoration - repositioned for the card */}
          <div className="absolute top-0 right-0 w-64 h-full bg-blue-500/5 blur-[80px] -mr-32 rounded-full" />

          <div className="px-5 py-3.5 relative z-10">
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
              <div className="flex items-center space-x-4 min-w-0">
                <button
                  onClick={() => navigate('/students')}
                  className={`p-2 ${colors.bgHoverStrong} rounded-xl transition-all duration-200 border border-white/5 hover:border-white/10 shadow-sm shrink-0`}
                >
                  <ArrowLeft className={`w-4 h-4 ${colors.textPrimary}`} />
                </button>
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="relative shrink-0">
                    {student.profilePicture ? (
                      <img
                        src={student.profilePicture}
                        alt={student.name}
                        className="w-12 h-12 rounded-xl object-cover border-2 border-blue-500/20 shadow-xl shadow-black/20"
                      />
                    ) : (
                      <div className={`w-14 h-14 ${colors.bgHoverStrong} rounded-xl flex items-center justify-center border border-white/10 shadow-xl shadow-black/20`}>
                        <User className={`w-7 h-7 ${colors.textTertiary}`} />
                      </div>
                    )}
                    <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 ${isDark ? 'border-[#09090b]' : 'border-white'} ${student.studentStatus === 'active' ? 'bg-green-500' : 'bg-red-500'} shadow-lg`} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <h1 className={`text-xl font-bold ${colors.textPrimary} tracking-tight truncate`}>
                        {student.name} {student.surname}
                      </h1>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                      <div className="flex items-center gap-1.5 overflow-hidden">
                        <Mail className="w-3 h-3 text-blue-500 shrink-0" />
                        <span className={`text-xs font-medium ${colors.textSecondary} truncate`}>{student.email}</span>
                      </div>
                      {student.phone && (
                        <div className="flex items-center gap-1.5">
                          <Phone className="w-3 h-3 text-blue-500 shrink-0" />
                          <span className={`text-xs font-medium ${colors.textSecondary}`}>{student.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-center gap-4 border-t lg:border-t-0 lg:border-l border-white/5 pt-3 lg:pt-0 lg:pl-4">
                <div className={`inline-flex p-0.5 rounded-lg ${colors.bgHoverStrong} border border-white/5 shadow-sm backdrop-blur-md`}>
                  {[
                    { key: 'packages', label: t('studentProfile.tabs.packages'), icon: Package },
                    { key: 'measurements', label: t('studentProfile.tabs.measurements'), icon: Ruler },
                    { key: 'details', label: t('studentProfile.tabs.details'), icon: User }
                  ].map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={`relative flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px] font-bold transition-all duration-200 whitespace-nowrap group ${activeTab === key
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-500/10'
                        : `${colors.textSecondary} hover:text-white hover:bg-white/5`
                        }`}
                    >
                      <Icon className={`w-3 h-3 ${activeTab === key ? 'text-white' : 'group-hover:scale-110 transition-transform'}`} />
                      <span className="tracking-tight">{label}</span>
                    </button>
                  ))}
                </div>

                <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 sm:gap-1 shrink-0 ml-auto lg:ml-0">
                  <div className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest border transition-all duration-300 ${student.studentStatus === 'active'
                    ? 'bg-green-500/10 text-green-500 border-green-500/20'
                    : 'bg-red-500/10 text-red-500 border-red-500/20'
                    }`}>
                    {getStatusLabel(student.studentStatus || 'active')}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] font-bold ${colors.textTertiary} uppercase tracking-tighter`}>{t('studentProfile.packages.registered', 'Kayıt') || 'Kayıt'}:</span>
                    <span className={`text-[10px] font-bold ${colors.textSecondary}`}>{moment(student.createdAt).format('DD/MM/YYYY')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="px-6 py-3">
          <div className={`bg-red-50 border ${colors.inputBorder} rounded-xl p-4 flex items-center`}>
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Removed old Tabs section */}

      {/* Tab Content */}
      <div className="p-3 sm:p-6">
        {activeTab === 'packages' && (
          <div className="space-y-6">
            {/* Header with Add Button */}
            <div className="flex justify-between items-center">
              <h2 className={`text-lg font-medium ${colors.textPrimary}`}>{t('studentProfile.packages.title')}</h2>
              {hasPermission('create:subscriptions') && (
                <button
                  onClick={() => setIsPackageModalOpen(true)}
                  className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  <span>{t('studentProfile.packages.assignPackage')}</span>
                </button>
              )}
            </div>

            {/* Packages List (Table) */}
            {(() => {
              const nonDemoSubscriptions = subscriptions.filter(sub => sub.packageName?.toLowerCase() !== 'demo');
              const activeSubscriptions = nonDemoSubscriptions.filter(sub => sub.status === 'active');
              const otherSubscriptions = nonDemoSubscriptions.filter(sub => sub.status !== 'active');

              const renderSubscriptionTable = (subsList, title) => {
                if (!subsList || subsList.length === 0) return null;
                return (
                  <div className="mb-8 last:mb-0">
                    {title && <h3 className={`text-sm font-bold uppercase tracking-wider ${colors.textPrimary} mb-4 ml-1 opacity-80`}>{title}</h3>}
                    <div className={`${colors.cardBg} rounded-2xl border ${colors.tableBorder} overflow-hidden shadow-sm`}>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className={`${colors.tableHeader} border-b ${colors.tableBorder}`}>
                              <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                {t('studentProfile.packages.table.package', 'Paket')}
                              </th>
                              <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                {t('studentProfile.packages.table.progress', 'Seans İlerlemesi')}
                              </th>
                              <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                {t('studentProfile.packages.table.payment', 'Ödeme Durumu')}
                              </th>
                              <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                {t('studentProfile.packages.table.dates', 'Seans Tarihleri')}
                              </th>
                              <th className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right">
                                {t('studentProfile.packages.table.actions', 'İşlemler')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/[0.04]">
                            {subsList.map((subscription) => {
                              const stats = getSubscriptionStats(subscription);
                              const progressPercent = stats.totalCapacity > 0
                                ? Math.min(100, Math.round((stats.used / stats.totalCapacity) * 100))
                                : 0;
                              const paymentPercent = subscription.totalPrice > 0
                                ? Math.min(100, (subscription.paidAmount / subscription.totalPrice) * 100)
                                : 0;

                              return (
                                <tr
                                  key={subscription.id}
                                  className={`group hover:bg-white/[0.02] transition-all duration-150`}
                                >
                                  <td className="px-6 py-5">
                                    <div className="flex flex-col">
                                      <span className={`text-sm font-bold ${colors.textPrimary} mb-1.5`}>
                                        {subscription.packageName}
                                      </span>
                                      <div className="flex items-center space-x-2">
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${getStatusBadge(subscription.status)}`}>
                                          {getStatusLabel(subscription.status)}
                                        </span>
                                        {user?.role !== 'trainer' && (
                                          <span className={`text-[11px] ${colors.textMuted} font-medium`}>
                                            ₺{(subscription.totalPrice / subscription.totalSessions).toFixed(2)} / s.
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </td>

                                  <td className="px-6 py-5 min-w-[180px]">
                                    <div className="flex flex-col space-y-2">
                                      <div className="flex justify-between items-end">
                                        <span className={`text-[11px] font-bold ${colors.textPrimary}`}>
                                          {stats.used} / {stats.totalCapacity}
                                        </span>
                                        <span className="text-[10px] font-bold text-blue-500">{progressPercent}%</span>
                                      </div>
                                      <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                                        <div
                                          className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                          style={{ width: `${progressPercent}%` }}
                                        />
                                      </div>
                                      <div className="flex items-center gap-2 text-[9px] font-semibold pt-0.5">
                                        <span className="text-emerald-500" title="Tamamlandı">
                                          ✓ {stats.breakdown.completed}
                                        </span>
                                        <span className={colors.textTertiary}>·</span>
                                        <span className="text-sky-400" title="Planlandı">
                                          ◷ {stats.breakdown.scheduled}
                                        </span>
                                        <span className={colors.textTertiary}>·</span>
                                        <span className="text-amber-500" title="İptal Edildi">
                                          ✗ {stats.breakdown.cancelled}
                                        </span>
                                        <span className={colors.textTertiary}>·</span>
                                        <span className="text-orange-600 dark:text-orange-500" title="Gelmedi">
                                          ⊘ {stats.breakdown.noShow}
                                        </span>
                                      </div>
                                    </div>
                                  </td>

                                  <td className="px-6 py-5 min-w-[200px]">
                                    <div className="flex flex-col space-y-2">
                                      <div className="flex justify-between items-center">
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${getStatusBadge(subscription.paymentStatus, 'payment')}`}>
                                          {getStatusLabel(subscription.paymentStatus)}
                                        </span>
                                        {user?.role !== 'trainer' && (
                                          <span className={`text-[11px] font-bold ${colors.textPrimary}`}>
                                            ₺{subscription.paidAmount} / ₺{subscription.totalPrice}
                                          </span>
                                        )}
                                      </div>
                                      <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                                        <div
                                          className={`h-full rounded-full transition-all duration-500 ${subscription.paymentStatus === 'paid' ? 'bg-green-500' : 'bg-amber-500'
                                            }`}
                                          style={{ width: `${paymentPercent}%` }}
                                        />
                                      </div>
                                    </div>
                                  </td>

                                  <td className="px-6 py-5">
                                    <div className="flex flex-col space-y-1">
                                      <div className="flex items-center space-x-2 text-[11px]">
                                        <Calendar className="w-3 h-3 text-slate-500" />
                                        <span className={colors.textSecondary}>{t('studentProfile.packages.firstSession', 'İlk')}:</span>
                                        <span className={`font-bold ${colors.textPrimary}`}>{getFirstSessionDate(subscription.id)}</span>
                                      </div>
                                      <div className="flex items-center space-x-2 text-[11px]">
                                        <Clock className="w-3 h-3 text-slate-500" />
                                        <span className={colors.textSecondary}>{t('studentProfile.packages.expires', 'Bitiş')}:</span>
                                        <span className={`font-bold ${colors.textPrimary}`}>{moment(subscription.endDate).format('DD/MM/YYYY')}</span>
                                      </div>
                                    </div>
                                  </td>

                                  <td className="px-6 py-5 text-right">
                                    <div className="flex items-center justify-end space-x-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
                                      <button
                                        onClick={() => handleViewSessions(subscription)}
                                        className="p-2 bg-blue-600/10 text-blue-500 hover:bg-blue-600 hover:text-white rounded-lg transition-all"
                                        title={t('studentProfile.packages.viewSessions')}
                                      >
                                        <List className="w-4 h-4" />
                                      </button>
                                      {hasPermission('update:subscriptions') && (
                                        <button
                                          onClick={() => handlePayment(subscription)}
                                          className="p-2 bg-green-600/10 text-green-500 hover:bg-green-600 hover:text-white rounded-lg transition-all"
                                          title={t('studentProfile.packages.addPayment')}
                                        >
                                          <CreditCard className="w-4 h-4" />
                                        </button>
                                      )}
                                      {hasPermission('update:subscriptions') && (
                                        <button
                                          onClick={() => handleEditSubscription(subscription)}
                                          className="p-2 bg-amber-600/10 text-amber-500 hover:bg-amber-600 hover:text-white rounded-lg transition-all"
                                          title={t('studentProfile.packages.edit')}
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                      )}
                                      {hasPermission('delete:subscriptions') && (
                                        <button
                                          onClick={() => handleDeleteSubscription(subscription.id)}
                                          className="p-2 bg-red-600/10 text-red-500 hover:bg-red-600 hover:text-white rounded-lg transition-all"
                                          title={t('studentProfile.packages.delete')}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              };

              if (nonDemoSubscriptions.length === 0) {
                return (
                  <div className={`text-center py-12 ${colors.cardBg} rounded-xl border ${colors.inputBorder}`}>
                    <Package className={`w-12 h-12 ${colors.textSecondary} mx-auto mb-4`} />
                    <h3 className={`text-lg font-medium ${colors.textPrimary} mb-2`}>{t('studentProfile.packages.noPackages')}</h3>
                    <p className={`${colors.textSecondary} mb-6`}>{t('studentProfile.packages.noPackagesDesc')}</p>
                    {hasPermission('create:subscriptions') && (
                      <button
                        onClick={() => setIsPackageModalOpen(true)}
                        className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                      >
                        {t('studentProfile.packages.assignFirst')}
                      </button>
                    )}
                  </div>
                );
              }

              return (
                <div>
                  {renderSubscriptionTable(activeSubscriptions, t('organizationDetailsPage.packagesSection.statuses.active', 'Aktif Olanlar'))}
                  {renderSubscriptionTable(otherSubscriptions, t('studentProfile.packages.others', 'Diğerleri'))}
                </div>
              );
            })()}
          </div>
        )}

        {activeTab === 'measurements' && (
          <StudentMeasurementsTab student={student} organizationId={organizationId} />
        )}

        {activeTab === 'details' && (
          <StudentDetailsTab
            student={student}
            organizationId={organizationId}
            onStudentUpdate={(updatedStudent) => setStudent(updatedStudent)}
          />
        )}
      </div>

      {/* Modals */}
      <StudentPackageAssignmentModal
        isOpen={isPackageModalOpen}
        onClose={() => setIsPackageModalOpen(false)}
        student={student}
        organizationId={student?.organizationId || organizationId}
        onAssignmentComplete={handlePackageAssigned}
      />

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        subscription={selectedSubscription ? { ...selectedSubscription, studentName: `${student.name} ${student.surname}` } : null}
        onPaymentComplete={handlePaymentComplete}
      />

      {/* Edit Subscription Modal */}
      {isEditModalOpen && editingSubscription && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${colors.cardBg} rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto`}>
            <div className={`flex items-center justify-between p-6 border-b ${colors.tableBorder}`}>
              <h2 className={`text-lg font-semibold ${colors.textPrimary}`}>{t('studentProfile.editSubscription.title')}</h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className={`${colors.textSecondary} hover:text-gray-500`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {[
                  { key: 'status', label: t('studentProfile.editSubscription.labels.status'), type: 'select', options: ['active', 'suspended', 'completed', 'cancelled'] },
                  { key: 'totalPrice', label: t('studentProfile.editSubscription.labels.totalPrice'), type: 'number' },
                  { key: 'paidAmount', label: t('studentProfile.editSubscription.labels.paidAmount'), type: 'number', readOnly: true },
                  { key: 'totalSessions', label: t('studentProfile.editSubscription.labels.totalSessions'), type: 'number' },
                  { key: 'remainingSessions', label: t('studentProfile.editSubscription.labels.remainingSessions'), type: 'number', readOnly: true },
                  { key: 'bonusSessions', label: t('studentProfile.editSubscription.labels.bonusSessions'), type: 'number' },
                  { key: 'endDate', label: t('studentProfile.editSubscription.labels.endDate'), type: 'date' }
                ].map(({ key, label, type, options, readOnly }) => (
                  <div key={key}>
                    <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>{label}</label>
                    {type === 'select' ? (
                      <select
                        value={editingSubscription[key]}
                        onChange={(e) => setEditingSubscription({ ...editingSubscription, [key]: e.target.value })}
                        className={`w-full px-3 py-2 border ${colors.inputBorder} ${colors.inputBg} rounded-xl focus:outline-none focus:ring-2 ${colors.inputFocus} ${colors.inputText}`}
                      >
                        {options.map(option => (
                          <option key={option} value={option}>{getStatusLabel(option)}</option>
                        ))}
                      </select>
                    ) : type === 'textarea' ? (
                      <textarea
                        value={editingSubscription[key] || ''}
                        onChange={(e) => setEditingSubscription({ ...editingSubscription, [key]: e.target.value })}
                        className={`w-full px-3 py-2 border ${colors.inputBorder} ${colors.inputBg} rounded-xl focus:outline-none focus:ring-2 ${colors.inputFocus} ${colors.inputText} ${colors.inputPlaceholder}`}
                        rows="3"
                        placeholder={`Enter ${label.toLowerCase()}...`}
                      />
                    ) : (
                      <input
                        type={type}
                        value={type === 'date' && editingSubscription[key]
                          ? editingSubscription[key].split('T')[0]
                          : (editingSubscription[key] ?? '')}
                        onChange={(e) => setEditingSubscription({
                          ...editingSubscription,
                          [key]: type === 'number' ? e.target.value : e.target.value
                        })}
                        onBlur={(e) => {
                          if (type === 'number') {
                            const parsed = parseFloat(e.target.value);
                            setEditingSubscription(prev => ({
                              ...prev,
                              [key]: isNaN(parsed) ? '' : parsed
                            }));
                          }
                        }}
                        className={`w-full px-3 py-2 border ${colors.inputBorder} ${colors.inputBg} rounded-xl focus:outline-none focus:ring-2 ${colors.inputFocus} ${colors.inputText} ${readOnly ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : ''}`}
                        {...(type === 'number' && { min: 0, step: (key === 'totalPrice' || key === 'paidAmount') ? '0.01' : '1' })}
                        disabled={readOnly}
                      />
                    )}
                    {key === 'totalPrice' && (
                      <div className={`text-xs ${colors.textSecondary} mt-1`}>
                        {t('studentProfile.editSubscription.singleSessionPrice')}: ₺{(editingSubscription.totalPrice / editingSubscription.totalSessions).toFixed(2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className={`px-6 py-2.5 ${colors.btnSecondaryBg} ${colors.btnSecondaryBorder} ${colors.btnSecondaryText} rounded-xl ${colors.btnSecondaryHover} transition-all duration-200 font-semibold`}
                >
                  {t('studentProfile.editSubscription.cancel')}
                </button>
                <button
                  onClick={() => {
                    // paidAmount is read-only & derived from payments — only validate fields the
                    // admin can actually edit.
                    const requiredKeys = ['totalPrice', 'totalSessions'];
                    const missing = requiredKeys.some(k => editingSubscription[k] === '' || editingSubscription[k] === null || editingSubscription[k] === undefined);
                    if (missing) {
                      toast.error(t('studentProfile.editSubscription.requiredFields', 'Lütfen tüm zorunlu alanları doldurunuz.'));
                      return;
                    }
                    const totalPrice = parseFloat(editingSubscription.totalPrice) || 0;
                    const totalSessions = parseInt(editingSubscription.totalSessions) || 0;
                    const bonusSessions = parseInt(editingSubscription.bonusSessions) || 0;

                    // Strip server-managed fields. The server keeps paidAmount /
                    // remainingAmount / paymentStatus in sync with the actual finance
                    // transactions; sending them here causes drift.
                    const {
                      paidAmount: _omitPaid,
                      remainingAmount: _omitRemaining,
                      paymentStatus: _omitStatus,
                      ...rest
                    } = editingSubscription;

                    handleUpdateSubscription(editingSubscription.id, {
                      ...rest,
                      totalPrice,
                      totalSessions,
                      bonusSessions,
                    });
                  }}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                >
                  {t('studentProfile.editSubscription.update')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDetailsPage;